import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { formatDateDashDisplay } from "@/utils/dateUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import jsPDF from "jspdf";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import "jspdf-autotable";
import { formatDMY } from "@/lib/dmy";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FileSpreadsheet,
  Download,
  Printer,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  RefreshCw,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  Package,
  AlertCircle,
  FileText,
  Users,
  FileDown
} from "lucide-react";

const PendingSalesReportPage = () => {
  usePageTitle("Pending Sales Report");
  const { token, user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const canSeeCosts = isAdmin;
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'total_orders', direction: 'desc' });
  const [expandedPartners, setExpandedPartners] = useState(new Set());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exportMode, setExportMode] = useState("single"); // "single" or "partner-wise"

  const fetchReport = async () => {
    const isRefresh = reportData !== null;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/pending-sales${params.toString() ? `?${params}` : ''}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      toast.error("Error loading report");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Check URL params on mount
    const urlParams = new URLSearchParams(window.location.search);
    const from = urlParams.get('from');
    const to = urlParams.get('to');
    if (from) setDateFrom(from);
    if (to) setDateTo(to);
  }, []);

  useEffect(() => {
    if (token) fetchReport();
  }, [token, dateFrom, dateTo]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-amber-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-amber-600" />;
  };

  const togglePartner = (code) => {
    setExpandedPartners(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  const filteredAndSortedPartners = useMemo(() => {
    if (!reportData?.partners) return [];
    
    let filtered = reportData.partners;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.client_code?.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      
      if (sortConfig.key === 'client_code') {
        return sortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  }, [reportData, searchTerm, sortConfig]);

  const exportToCSV = (partnerCode = null, includeAmounts = true) => {
    if (!reportData?.partners) return;
    
    const partnersToExport = partnerCode 
      ? filteredAndSortedPartners.filter(p => p.client_code === partnerCode)
      : filteredAndSortedPartners;
    
    const allRows = [];
    
    partnersToExport.forEach(partner => {
      // Add partner header
      if (includeAmounts) {
        allRows.push([`Partner: ${partner.client_code}`, `Total Orders: ${partner.total_orders}`, `Total Value: ${partner.total_value}`]);
        allRows.push(["Tracking", "Order Date", "Product", "Qty", "Medicine Cost", "Shipping Cost", "Total Cost"]);
      } else {
        allRows.push([`Partner: ${partner.client_code}`, `Total Orders: ${partner.total_orders}`]);
        allRows.push(["Tracking", "Order Date", "Product", "Qty", "Service", "Country"]);
      }
      
      // Add orders
      partner.orders?.forEach(order => {
        if (includeAmounts) {
          allRows.push([
            order.tracking,
            order.order_date,
            order.product,
            order.qty,
            order.medicine_cost,
            order.shipping_cost,
            order.total_cost
          ]);
        } else {
          allRows.push([
            order.tracking,
            order.order_date,
            order.product,
            order.qty,
            order.service || '',
            order.country || ''
          ]);
        }
      });
      
      allRows.push([]); // Empty row between partners
    });
    
    const csvContent = allRows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const amountSuffix = includeAmounts ? "" : "_no_amounts";
    a.download = partnerCode 
      ? `pending_sales_${partnerCode}${amountSuffix}_${format(new Date(), 'yyyy-MM-dd')}.csv`
      : `pending_sales_report${amountSuffix}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(partnerCode ? `Exported ${partnerCode} data!` : "Report exported!");
  };

  const exportAllPartnerWise = (includeAmounts = true) => {
    filteredAndSortedPartners.forEach(partner => {
      if (partner.orders?.length > 0) {
        exportToCSV(partner.client_code, includeAmounts);
      }
    });
    toast.success(`Exported ${filteredAndSortedPartners.length} partner files!`);
  };

  // PDF Export Functions
  const exportToPDF = (partnerCode = null) => {
    if (!reportData?.partners) return;
    
    const partnersToExport = partnerCode 
      ? filteredAndSortedPartners.filter(p => p.client_code === partnerCode)
      : filteredAndSortedPartners;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Pending Sales Report", pageWidth / 2, 20, { align: "center" });
    
    // Subtitle
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const dateRangeText = dateFrom || dateTo 
      ? `Period: ${dateFrom || 'Start'} to ${dateTo || 'Today'}`
      : 'All Time';
    doc.text(dateRangeText, pageWidth / 2, 28, { align: "center" });
    doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy, HH:mm')}`, pageWidth / 2, 34, { align: "center" });
    
    // Summary
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Summary", 14, 45);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Total Pending Partners: ${reportData.summary?.total_pending_partners || 0}`, 14, 52);
    doc.text(`Total Pending Orders: ${reportData.summary?.total_pending_orders || 0}`, 14, 58);
    doc.text(`Total Pending Value: ₹${(reportData.summary?.total_pending_value || 0).toLocaleString()}`, 14, 64);
    
    let yPos = 75;
    
    partnersToExport.forEach((partner, idx) => {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      // Partner header
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setFillColor(255, 237, 213); // Amber light
      doc.rect(14, yPos - 5, pageWidth - 28, 8, 'F');
      doc.text(`Partner: ${partner.client_code}`, 16, yPos);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(`Orders: ${partner.total_orders} | Value: ₹${partner.total_value?.toLocaleString()}`, pageWidth - 14, yPos, { align: "right" });
      
      yPos += 10;
      
      // Orders table
      if (partner.orders && partner.orders.length > 0) {
        const tableData = partner.orders.slice(0, 50).map(order => [
          order.tracking || '-',
          order.order_date || '-',
          (order.product || '-').substring(0, 25),
          order.qty || '-',
          order.service || '-',
          order.country || '-'
        ]);
        
        doc.autoTable({
          startY: yPos,
          head: [['Tracking', 'Date', 'Product', 'Qty', 'Service', 'Country']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [217, 119, 6], fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 22 },
            2: { cellWidth: 50 },
            3: { cellWidth: 15 },
            4: { cellWidth: 20 },
            5: { cellWidth: 25 }
          },
          margin: { left: 14, right: 14 }
        });
        
        yPos = doc.lastAutoTable.finalY + 15;
      } else {
        doc.setFontSize(9);
        doc.text("No order details available", 14, yPos);
        yPos += 15;
      }
    });
    
    // Footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
    }
    
    const filename = partnerCode 
      ? `pending_sales_${partnerCode}_${format(new Date(), 'yyyy-MM-dd')}.pdf`
      : `pending_sales_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    
    doc.save(filename);
    toast.success(partnerCode ? `PDF exported for ${partnerCode}!` : "PDF report exported!");
  };

  const exportAllPartnerWisePDF = () => {
    filteredAndSortedPartners.forEach(partner => {
      if (partner.orders?.length > 0) {
        exportToPDF(partner.client_code);
      }
    });
    toast.success(`Exported ${filteredAndSortedPartners.length} partner PDFs!`);
  };

  const handlePrint = () => {
    window.print();
  };

  const applyDateFilter = (preset) => {
    const today = new Date();
    let from = "";
    let to = format(today, "yyyy-MM-dd");
    
    switch (preset) {
      case "30days":
        from = format(new Date(today.setDate(today.getDate() - 30)), "yyyy-MM-dd");
        break;
      case "thisMonth":
        from = format(new Date(today.getFullYear(), today.getMonth(), 1), "yyyy-MM-dd");
        break;
      case "lastMonth":
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        from = format(lastMonth, "yyyy-MM-dd");
        to = format(new Date(today.getFullYear(), today.getMonth(), 0), "yyyy-MM-dd");
        break;
      case "all":
        from = "";
        to = "";
        break;
    }
    
    setDateFrom(from);
    setDateTo(to);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 print:p-2">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          table { font-size: 10px; }
          th, td { padding: 4px 6px !important; }
        }
      `}</style>

      {/* Header - Non-printable controls */}
      <div className="no-print sticky top-0 z-20 bg-white dark:bg-gray-800 border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <FileSpreadsheet className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Pending Sales Report</h1>
                <p className="text-sm text-muted-foreground">
                  Partners who have shipments but haven't uploaded sales data
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading || refreshing}>
                <RefreshCw className={`w-4 h-4 mr-2 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Select value={exportMode} onValueChange={setExportMode}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single File</SelectItem>
                  <SelectItem value="partner-wise">Partner-wise</SelectItem>
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canSeeCosts ? (
                    <>
                      <DropdownMenuItem onClick={() => exportMode === "single" ? exportToCSV(null, true) : exportAllPartnerWise(true)}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        CSV with Amounts {exportMode === "partner-wise" && "(Partner-wise)"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportMode === "single" ? exportToCSV(null, false) : exportAllPartnerWise(false)}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        CSV without Amounts {exportMode === "partner-wise" && "(Partner-wise)"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => exportMode === "single" ? exportToPDF() : exportAllPartnerWisePDF()}>
                        <FileDown className="w-4 h-4 mr-2" />
                        Export PDF {exportMode === "partner-wise" && "(Partner-wise)"}
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={() => exportMode === "single" ? exportToCSV(null, false) : exportAllPartnerWise(false)}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Export CSV {exportMode === "partner-wise" && "(Partner-wise)"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportMode === "single" ? exportToPDF() : exportAllPartnerWisePDF()}>
                        <FileDown className="w-4 h-4 mr-2" />
                        Export PDF {exportMode === "partner-wise" && "(Partner-wise)"}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handlePrint}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print Report
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          {/* Date Filters */}
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Quick Filter:</Label>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => applyDateFilter("all")} 
                  className={!dateFrom && !dateTo ? "bg-amber-100" : ""}>All Time</Button>
                <Button variant="outline" size="sm" onClick={() => applyDateFilter("30days")}>Last 30 Days</Button>
                <Button variant="outline" size="sm" onClick={() => applyDateFilter("thisMonth")}>This Month</Button>
                <Button variant="outline" size="sm" onClick={() => applyDateFilter("lastMonth")}>Last Month</Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">From:</Label>
              <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">To:</Label>
              <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" />
            </div>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div className={`max-w-[1600px] mx-auto p-6 print:p-2 print:max-w-none relative ${refreshing ? 'opacity-50 pointer-events-none' : ''}`}>
        {refreshing && (
          <div className="absolute inset-0 flex items-start justify-center pt-24 z-10">
            <div className="flex items-center gap-2 bg-white shadow-lg rounded-lg px-4 py-3 border">
              <RefreshCw className="w-4 h-4 animate-spin text-amber-600" />
              <span className="text-sm font-medium text-slate-600">Updating report...</span>
            </div>
          </div>
        )}
        {/* Print Header */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold text-center">Pending Sales Report</h1>
          <p className="text-center text-gray-600">
            {dateFrom || dateTo ? `Period: ${dateFrom || 'Start'} to ${dateTo || 'Today'}` : 'All Time'}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                <p className="text-xs font-medium text-amber-600 uppercase">Pending Partners</p>
              </div>
              <p className="text-3xl font-bold text-amber-700 mt-2">
                {reportData?.summary?.total_pending_partners || 0}
              </p>
              <p className="text-xs text-amber-500 mt-1">Partners without sales uploads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                <p className="text-xs font-medium text-muted-foreground uppercase">Pending Orders</p>
              </div>
              <p className="text-3xl font-bold mt-2">
                {(reportData?.summary?.total_pending_orders || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Total orders without sales data</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-xs font-medium text-muted-foreground uppercase">Pending Value</p>
              </div>
              {canSeeCosts ? (
                <>
                  <p className="text-3xl font-bold mt-2">
                    ₹{(reportData?.summary?.total_pending_value || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Total value missing sales data</p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold mt-2 text-muted-foreground">--</p>
                  <p className="text-xs text-muted-foreground mt-1">Restricted</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="no-print flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by partner code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Badge variant="outline" className="px-3 py-1">
            {filteredAndSortedPartners.length} partners
          </Badge>
        </div>

        {/* Partners Table */}
        <Card>
          <CardContent className="p-0">
            {filteredAndSortedPartners.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">No Pending Sales Found</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  All partners have submitted their sales data for this period.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50 dark:bg-amber-950/30">
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="cursor-pointer hover:bg-amber-100/50" onClick={() => handleSort('client_code')}>
                      <div className="flex items-center">
                        Partner Code
                        <SortIcon columnKey="client_code" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-amber-100/50" onClick={() => handleSort('total_orders')}>
                      <div className="flex items-center">
                        Pending Orders
                        <SortIcon columnKey="total_orders" />
                      </div>
                    </TableHead>
                    {canSeeCosts && (
                      <TableHead className="cursor-pointer hover:bg-amber-100/50" onClick={() => handleSort('total_value')}>
                        <div className="flex items-center">
                          Total Value
                          <SortIcon columnKey="total_value" />
                        </div>
                      </TableHead>
                    )}
                    <TableHead>First Order</TableHead>
                    <TableHead>Last Order</TableHead>
                    <TableHead className="no-print">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedPartners.map((partner) => (
                    <Collapsible key={partner.client_code} asChild>
                      <>
                        <TableRow className="hover:bg-amber-50/50 cursor-pointer" onClick={() => togglePartner(partner.client_code)}>
                          <TableCell>
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                                {expandedPartners.has(partner.client_code) 
                                  ? <ChevronDown className="w-4 h-4" />
                                  : <ChevronRight className="w-4 h-4" />
                                }
                              </Button>
                            </CollapsibleTrigger>
                          </TableCell>
                          <TableCell className="font-medium">{partner.client_code}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-amber-50">{partner.total_orders}</Badge>
                              {partner.is_partial && (
                                <span className="text-xs text-muted-foreground">
                                  of {partner.total_shipment_orders} ({partner.uploaded_orders} uploaded)
                                </span>
                              )}
                              {!partner.is_partial && partner.total_shipment_orders > 0 && (
                                <span className="text-xs text-red-500 font-medium">
                                  (0 uploaded)
                                </span>
                              )}
                            </div>
                          </TableCell>
                          {canSeeCosts && (
                            <TableCell className="font-medium">₹{partner.total_value?.toLocaleString()}</TableCell>
                          )}
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateDashDisplay(partner.first_order_date)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateDashDisplay(partner.last_order_date)}
                          </TableCell>
                          <TableCell className="no-print">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()}>
                                  <Download className="w-3 h-3 mr-1" />
                                  Export
                                  <ChevronDown className="w-3 h-3 ml-1" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canSeeCosts ? (
                                  <>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportToCSV(partner.client_code, true); }}>
                                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                                      CSV with Amounts
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportToCSV(partner.client_code, false); }}>
                                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                                      CSV without Amounts
                                    </DropdownMenuItem>
                                  </>
                                ) : (
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportToCSV(partner.client_code, false); }}>
                                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                                    CSV
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportToPDF(partner.client_code); }}>
                                  <FileDown className="w-4 h-4 mr-2" />
                                  PDF
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                        <CollapsibleContent asChild>
                          <TableRow>
                            <TableCell colSpan={canSeeCosts ? 7 : 6} className="p-0 bg-gray-50 dark:bg-gray-900">
                              <div className="p-4">
                                <h4 className="font-medium mb-2 text-sm">Order Details for {partner.client_code}</h4>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="text-xs">
                                      <TableHead>Tracking</TableHead>
                                      <TableHead>Order Date</TableHead>
                                      <TableHead>Product</TableHead>
                                      <TableHead>Qty</TableHead>
                                      <TableHead>Service</TableHead>
                                      <TableHead>Country</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {partner.orders?.slice(0, 20).map((order, idx) => (
                                      <TableRow key={idx} className="text-xs">
                                        <TableCell className="font-mono">{order.tracking || '-'}</TableCell>
                                        <TableCell>{formatDMY(order.order_date)}</TableCell>
                                        <TableCell className="max-w-[200px] truncate">{order.product || '-'}</TableCell>
                                        <TableCell>{order.qty || '-'}</TableCell>
                                        <TableCell><Badge variant="outline" className="text-xs">{order.service || '-'}</Badge></TableCell>
                                        <TableCell>{order.country || '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                    {partner.orders?.length > 20 && (
                                      <TableRow>
                                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                                          ... and {partner.orders.length - 20} more orders
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PendingSalesReportPage;
